import * as React from "react";
import { DateHelper } from "../../../../../../react-core/Helpers/DateHelper";
import { BaseComponent } from "../../../../../../react-core/Html/BaseComponent";
import { addDays, addMonths, addYears } from "../../../../../../react-core/utils/date";
import { JsonDateParser } from "../../../../../../react-core/Utils/JsonDateParser";
import { formatMoney } from "../../../../../../react-core/Utils/Money";
import { ObjectComparer } from "../../../../../../react-core/Utils/ObjectComparer";
import { CurrencyCode } from "../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { ServiceProposition } from "../../../../../../Wrap/Constants/Enums/ServiceProposition";
import { ExternalTransferStatus } from "../../../../../../Wrap/Constants/Enums/Transfer/ExternalTransferStatus";
import { AssetUnderManagementModel } from "../../../../../../Wrap/Models/Models/Business/AssetUnderManagement/AssetUnderManagementModel";
import LineChart from "../../../../../Shared/Chart/LineChart/LineChart.component";
import { Alert } from "../../../../../Shared/Components/Alert";
import { DateRange } from "../../../../../Shared/Components/DateRange";
import { AlertType } from "../../../../../Shared/Entities/Constants/AlertConstants";
import AdviserSiteTagManager, {
    eventCategoryForGTM,
} from "../../../../../Shared/Cookies/ChoicesCookie/AdviserSiteTagManager";
import { LoadingSkeletonType } from "../../../../../Shared/LoadingSkeleton/Contents";
import { LoadingSkeleton } from "../../../../../Shared/LoadingSkeleton/LoadingSkeleton";
import { getQueryParameters } from "../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { BusinessManagementAction } from "../../Action/BusinessManagementAction";
import { AumContent } from "../../Resource/Content";
import { BusinessManagementStore } from "../../Store/Variable";
import AssetHoldingsTable, { IAssetHoldingsTableRow } from "./AssetHoldingsTable";

import AumBreakdown from "./AumBreakdown";
import { UserStore } from "../../../../Stroes/Variable";
import { CommonHelper } from "../../../../../../react-core/Helpers/CommonHelper";
import { DateOptions } from "../../../../../Shared/Components/Resource/Contents";

interface IAumProps extends React.Props<Aum> {
    selectedProposition: ServiceProposition[];
}

interface IAumState {
    data: AssetUnderManagementModel;
    isLoading: boolean;
    from: Date;
    to: Date;
    dateSelected: string;
}

export class Aum extends BaseComponent<IAumProps, IAumState> {
    constructor(props: IAumProps) {
        super(props);
        this.state = {
            data: null,
            isLoading: true,
            from: addMonths(new Date(), -3),
            to: addDays(new Date(), -1),
            dateSelected: "6",
        };
        this.subscribeToStore(BusinessManagementStore, () => this.onStoreChange());
    }

    componentDidMount() {
        this.search(this.state.from, this.state.to, this.props.selectedProposition)
        this.addEvent({
            environment: {
                appType: "web",
                setting: "prod",
                timestamps: JsonDateParser.toJsonDateNoOffset(new Date()).toString(),
            },
            page: {
                pageUrl: window.location.pathname.includes("aum")
                    ? window.location.pathname
                    : window.location.pathname + "/aum",
                pageTitle: `${document.title} - dashboard - aum`,
            },
            user: {
                loginId: UserStore.getUserInfo()?.ClientId.toString(),
                internal: "yes",
            },
            event: "pageLoaded",
        });
    }

    onStoreChange() {
        const data = BusinessManagementStore.getData();
        this.setState({ data, isLoading: false });
    }

    componentWillReceiveProps(nextProps) {
        if (ObjectComparer.arraysDiffer(nextProps.selectedProposition, this.props.selectedProposition)) {
            this.search(this.state.from, this.state.to, nextProps.selectedProposition);
        }
    }

    search(from, to, selectedProposition) {
        this.setState({ isLoading: true });
        BusinessManagementAction.GetCommonData(
            getQueryParameters()["firm"],
            JsonDateParser.toJsonDateNoOffset(addDays(new Date(), -1)),
            JsonDateParser.toJsonDateNoOffset(from),
            JsonDateParser.toJsonDateNoOffset(to),
            ExternalTransferStatus.Pending,
            selectedProposition
        );
    }

    resolveAssetHoldingsTableRows(): IAssetHoldingsTableRow[] {
        const rows: IAssetHoldingsTableRow[] = [];

        (this.state.data?.EndDateProductAssetHoldings || []).forEach((endDateHolding) => {
            const startDateHolding = (this.state.data?.StartDateProductAssetHoldings || []).find(
                (assetHolding) => assetHolding.ProductType == endDateHolding.ProductType
            );

            rows.push({
                startValue: startDateHolding?.Value?.Amount,
                endValue: endDateHolding?.Value?.Amount,
                subAccountType: endDateHolding.ProductType,
            });
        });

        return rows;
    }

    private addEvent = new AdviserSiteTagManager().addEvent;

    render() {
        return (
            <div id={"panel-AUM"} role="tabpanel" tabIndex={0} aria-labelledby={"tab-AUM"}>
                <h2>{AumContent.title}</h2>

                <AumBreakdown isLoading={this.state.isLoading} data={this.state.data?.AUMPerformanceTableData} />

                <Alert alertType={AlertType.Warning} message={AumContent.figuresWarning} />
                <div className="row">
                    <div className="col-12 col-sm-auto col-middle">
                        <DateRange
                            minDate={null}
                            fromDate={this.state.from}
                            toDate={this.state.to}
                            cssGridDate="col-12 col-sm-auto"
                            cssGridTo="col-12 col-sm-auto"
                            cssGridFrom="col-12 col-sm-auto"
                            cssGridLabel="col-12 col-sm-auto col-middle"
                            selectValue={this.state.dateSelected} // Past year
                            useShortMonthName = {true}
                            onChange={(from, to, fromInception, dateSelected) => {
                                if (dateSelected != this.state.dateSelected) {
                                    this.addEvent({
                                        event: "aumtab_interaction",
                                        eventParameters: {
                                            eventAction: "date range selector",
                                            eventCategory: eventCategoryForGTM.aumTab,
                                            eventLabel: CommonHelper.getOptionsTxt(
                                                dateSelected,
                                                DateOptions
                                            ).toLowerCase(),
                                        },
                                    });
                                }
                                this.setState({ from, to, dateSelected }, () =>
                                    this.search(from, to, this.props.selectedProposition)
                                );
                            }}
                            excludeToday
                            excludeSinceAccountOpening
                            maxDate={addDays(new Date(), -1)}
                        />
                    </div>
                </div>

                {this.state.isLoading ? (
                    <LoadingSkeleton type={LoadingSkeletonType.Chart} />
                ) : (
                    <div className="panel-subtle">
                        <div className="row">
                            <div className="col-12">
                                <LineChart
                                    xAxis={{
                                        type: "datetime",
                                    }}
                                    series={[
                                        {
                                            name: "Value",
                                            data:
                                                this.state.data?.AUMPerformanceGraphData?.map((dataPoint) => {
                                                    return {
                                                        xValue: dataPoint.DateForChart,
                                                        yValue: dataPoint.Value.Amount,
                                                    };
                                                }) || [],
                                        },
                                    ]}
                                    tooltipFormatter={(tooltipContext) => {
                                        const seriesName = tooltipContext.series.getName();
                                        const date = DateHelper.getDateStringAsddmmmyyyy(
                                            new Date(tooltipContext.x),
                                            true
                                        );
                                        const amount = `£${formatMoney(
                                            {
                                                Currency: CurrencyCode.GBP,
                                                Amount: tooltipContext.y,
                                            },
                                            true,
                                            2
                                        )}`;

                                        return `${seriesName}: ${amount} as at ${date}`;
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}
                {this.state.isLoading ? (
                    <div className="mt-20">
                        <LoadingSkeleton type={LoadingSkeletonType.Table} />
                    </div>
                ) : (
                    <div className="mt-40">
                        <h2>Asset holdings</h2>
                        <AssetHoldingsTable
                            asAtStart={this.state.from}
                            asAtEnd={this.state.to}
                            rows={this.resolveAssetHoldingsTableRows()}
                        />
                    </div>
                )}
            </div>
        );
    }
}
